import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from '../../contexts/TranslationContext';
import '../../assets/css/engineParams.scss';
import '../../config';
import api from '../../utils/api';

const _defaultEngineParams = [
  { Version: 'thanh', SMode: '0', DeltaX: '20', DeltaY: '25', Method: '0', TopResult: '100', TopGrouping: 100, isDefault: true },
  { Version: 'binh', SMode: '2', DeltaX: '20', DeltaY: '25', Method: '0', TopResult: '100', TopGrouping: 100 },
];

const _defaultType = 'primary';

const _defaultQuickSearchParams = {
  QuickSearchVersion: '',
  TopQuickSearch: 6500,
  EnableQuickSearch: true
}

const AnalyzeEngineParams = ({ params, onChange }) => {
  const { language } = useTranslation();
  const [engineVersions, setEngineVersions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const isInitialMount = useRef(true);
  const prevParamsRef = useRef(params);
  const hasInitialized = useRef(false); // Track if we've already initialized

  // Fetch available engine versions on component mount
  useEffect(() => {
    const fetchEngineVersions = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const response = await api.post(`${global.config.api.domain}/fwiq-api/api/search1n/getengines`, {});

        if (response.status !== 200) {
          throw new Error(`Failed to fetch engine versions: ${response.status}`);
        }

        const data = response.data;

        if (Array.isArray(data)) {
          const sortedVersions = data.sort((a, b) => b.EngineVersion.localeCompare(a.EngineVersion));
          setEngineVersions(sortedVersions);
        } else {
          setEngineVersions([]);
          console.warn('Unexpected response format for engine versions:', data);
        }
      } catch (err) {
        console.error('Error fetching engine versions:', err);
        setError(err.message);
        setEngineVersions([]);
      } finally {
        setIsLoading(false);
        isInitialMount.current = false;
      }
    };

    fetchEngineVersions();
  }, []); // Empty dependency array since we only want to fetch once on mount

  // Separate effect for initializing parameters when engines are loaded
  useEffect(() => {
    // Only auto-load if there's no Version at all, we have engines available, and we haven't already initialized
    if ((!params.Version || params.Version === "") && engineVersions.length > 0 && !hasInitialized.current) {
      const firstEngine = engineVersions.find(v => v.Type.toLowerCase() === _defaultType) || engineVersions[0];

      const firstEngineDefault = _defaultEngineParams.find(
        preset => firstEngine.EngineVersion.toLowerCase().indexOf(preset.Version.toLowerCase()) > -1
      ) || {};

      // Build new params, preserving any existing user values
      const newParams = {
        Version: firstEngine.EngineVersion,
        SMode: params.SMode || firstEngineDefault.SMode || _defaultEngineParams[0].SMode,
        DeltaX: params.DeltaX || firstEngineDefault.DeltaX || _defaultEngineParams[0].DeltaX,
        DeltaY: params.DeltaY || firstEngineDefault.DeltaY || _defaultEngineParams[0].DeltaY,
        Method: params.Method || firstEngineDefault.Method || _defaultEngineParams[0].Method,
        TopResult: params.TopResult || firstEngineDefault.TopResult || _defaultEngineParams[0].TopResult,
        TopGrouping: params.TopGrouping || firstEngineDefault.TopGrouping || 100,
        QuickSearchVersion: engineVersions[0].EngineVersion || params.QuickSearchVersion,
        TopQuickSearch: params.TopQuickSearch || _defaultQuickSearchParams.TopQuickSearch,
        EnableQuickSearch: params.EnableQuickSearch !== undefined ? params.EnableQuickSearch : _defaultQuickSearchParams.EnableQuickSearch
      };

      hasInitialized.current = true; // Mark as initialized
      onChange(newParams);
    }
  }, [engineVersions.length]); // eslint-disable-line react-hooks/exhaustive-deps

  // Update the handleInputChange function to check for defaults
  const handleInputChange = (e) => {
    
    const { name, value } = e.target;

    // Store a copy of the current params before modifying
    prevParamsRef.current = { ...params };

    

    // If changing the Version and it matches a default, apply all default values
    if (name === 'Version') {
      const matchingDefault = _defaultEngineParams.find(
        preset => value.toLowerCase().indexOf(preset.Version.toLowerCase()) > -1
      );

      if (matchingDefault) {
        // Apply all default parameters but keep the Version as selected
        // Preserve existing QuickSearchVersion, TopQuickSearch, and EnableQuickSearch
        onChange({
          ...params,
          ...matchingDefault,
          Version: value, // Keep the selected version, not the default version string
          QuickSearchVersion: params.QuickSearchVersion || value, // Keep existing QuickSearchVersion or use current Version
          TopQuickSearch: params.TopQuickSearch || _defaultQuickSearchParams.TopQuickSearch, // Keep existing TopQuickSearch or default
          EnableQuickSearch: params.EnableQuickSearch !== undefined ? params.EnableQuickSearch : _defaultQuickSearchParams.EnableQuickSearch // Keep existing EnableQuickSearch setting
        });
        return;
      }
    }

    // Normal case - just update the single parameter
    onChange({
      ...params,
      [name]: value
    });
  };

  return (
    <div className="engine-params">
      <h3 className="params-title">
        <i className="fa fa-cogs"></i>
        {language?.engineParams?.title || 'Engine Parameters'}
        {isLoading && <span className="loading-indicator"><i className="fa fa-circle-notch fa-spin"></i></span>}
      </h3>

      <div className="params-container">
        {/* Version Selection */}
        <div className="param-group">
          <label htmlFor="Version">
            {language?.engineParams?.version || 'Version'}
          </label>
          <select
            name="Version"
            value={params.Version || ''}
            onChange={handleInputChange}
            disabled={isLoading}
          >
            {engineVersions.length > 0 ? (
              engineVersions.map(version => {
                const id = version.EngineVersion;
                const name = version.EngineVersion;

                return (
                  <option key={id} value={id}>
                    {name}
                  </option>
                );
              })
            ) : (
              <option value="">{language?.engineParams?.loadingVersions || 'Loading versions...'}</option>
            )}
          </select>
          {error && <div className="param-error">{error}</div>}
        </div>

        {/* Speed Mode */}
        <div className="param-group param-group-row">
          <div className="param-col">
            <label htmlFor="SMode">
              {language?.engineParams?.speedMode || 'Speed Mode'}
            </label>
            <input
              name="SMode"
              value={params.SMode}
              onChange={(e) => {
                onChange({
                  ...params,
                  SMode: e.target.value
                });
              }}
              type="text"
            />
          </div>
          <div className="param-col">
            <label htmlFor="Method">
              {language?.engineParams?.method || 'Method'}
            </label>
            <select
              name="Method"
              value={params.Method}
              onChange={handleInputChange}
            >
              <option value="0" selected="selected">0 - {language?.engineParams?.methodAverage || 'Average'}</option>
              <option value="1">1 - {language?.engineParams?.methodMinimum || 'Minimum'}</option>
            </select>
          </div>
        </div>

        {/* Delta X and Y Group */}
        <div className="param-group param-group-row">
          <div className="param-col">
            <label htmlFor="DeltaX">
              {language?.engineParams?.deltaX || 'Delta X'}
            </label>
            <input
              type="number"
              name="DeltaX"
              value={params.DeltaX}
              onChange={handleInputChange}
              min="0"
              max="100"
              step="1"
            />
          </div>

          <div className="param-col">
            <label htmlFor="DeltaY">
              {language?.engineParams?.deltaY || 'Delta Y'}
            </label>
            <input
              type="number"
              name="DeltaY"
              value={params.DeltaY}
              onChange={handleInputChange}
              min="0"
              max="100"
              step="1"
            />
          </div>
        </div>

        {/* Top Results */}
        <div className="param-group param-group-row">
          <div className="param-col">
            <label htmlFor="TopResult">
              {language?.engineParams?.topResult || 'Top Results'}
            </label>
            <input
              type="number"
              name="TopResult"
              value={params.TopResult}
              onChange={handleInputChange}
              min="1"
              max="1000"
              step="1"
            />
          </div>
          <div className="param-col">
            <label htmlFor="TopGrouping">
              {language?.engineParams?.topGrouping || 'Top Grouping'}
            </label>
            <input
              type="number"
              name="TopGrouping"
              value={params.TopGrouping}
              onChange={handleInputChange}
              min="1"
              max="500"
              step="1"
            />
          </div>
        </div>

        {/* Quick Search Toggle */}
        <div className="param-group param-group-switch">
          <label className="switch-label">
            <input
              type="checkbox"
              name="EnableQuickSearch"
              checked={params.EnableQuickSearch || true}
              onChange={(e) => {
                onChange({
                  ...params,
                  EnableQuickSearch: e.target.checked,
                });
              }}
            />
            <span className={`switch-toggle ${params.EnableQuickSearch ? 'checked' : ''}`}>
              <span className={`switch-dot ${params.EnableQuickSearch ? 'checked' : ''}`}></span>
            </span>
            <span className="switch-text">
              {language?.engineParams?.enableQuickSearch || 'Enable Quick Search'}
            </span>
          </label>
        </div>

        {/* Quick Search Version - only show when enabled */}
        {params.EnableQuickSearch && (
          <div className="param-group">
            <label htmlFor="QuickSearchVersion">
              {language?.engineParams?.quickSearchVersion || 'Quick Search Version'}
            </label>
            <select
              name="QuickSearchVersion"
              value={params.QuickSearchVersion || ''}
              onChange={handleInputChange}
              disabled={isLoading}
            >
              {engineVersions.length > 0 ? (
                engineVersions.map(version => {
                  const id = version.EngineVersion;
                  const name = version.EngineVersion;

                  return (
                    <option key={id} value={id}>
                      {name}
                    </option>
                  );
                })
              ) : (
                <option value="">{language?.engineParams?.loadingVersions || 'Loading versions...'}</option>
              )}
            </select>
            {error && <div className="param-error">{error}</div>}
          </div>
        )}

        {/* Top Quick Search - only show when enabled */}
        {params.EnableQuickSearch && (
          <div className="param-group">
            <label htmlFor="TopQuickSearch">
              {language?.engineParams?.quickSearchTop || 'Top Quick Search Results'}
            </label>
            <input
              type="number"
              name="TopQuickSearch"
              value={params.TopQuickSearch}
              onChange={handleInputChange}
              min="1"
              step="100"
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default AnalyzeEngineParams;